import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import type { MapOptions } from 'leaflet';
import { Observable, map, of, switchMap, throwError } from 'rxjs';

export interface ArcgisSearchResult {
  lat: number;
  lng: number;
  label: string;
}

export interface ArcgisCoordinate {
  lat: number;
  lng: number;
}

export interface ArcgisReverseGeocodeResult {
  city: string;
  province: string;
}

export type ArcgisMapZoomPreset = 'withoutZoomRestrictions' | 'withZoomRestrictions';
export type ArcgisMapZoomOptions = Pick<MapOptions, 'minZoom' | 'maxZoom'>;

interface ArcgisCandidate {
  address: string;
  location: {
    x: number;
    y: number;
  };
  score: number;
}

interface ArcgisGeocodeResponse {
  candidates: ArcgisCandidate[];
}

interface ArcgisReverseGeocodeAddress {
  City?: string;
  District?: string;
  Subregion?: string;
  Region?: string;
}

interface ArcgisReverseGeocodeResponse {
  address?: ArcgisReverseGeocodeAddress;
}

interface ArcgisCountryAliases {
  code: string;
  aliases: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ArcgisMapService {
  private readonly coordinateDecimals = 6;
  private readonly satelliteTilesUrl =
    'https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
  private readonly geocodeUrl =
    'https://geocode-api.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates';
  private readonly reverseGeocodeUrl =
    'https://geocode-api.arcgis.com/arcgis/rest/services/World/GeocodeServer/reverseGeocode';
  private readonly browserRegionCountryCodes: Record<string, string> = {
    ES: 'ESP',
    MX: 'MEX',
    US: 'USA',
    GB: 'GBR',
    PT: 'PRT',
    FR: 'FRA',
    DE: 'DEU',
    IT: 'ITA',
    AR: 'ARG',
    CL: 'CHL',
    CO: 'COL',
    PE: 'PER',
  };
  private readonly browserLanguageCountryCodes: Record<string, string> = {
    es: 'ESP',
    pt: 'PRT',
    fr: 'FRA',
    de: 'DEU',
    it: 'ITA',
  };
  private readonly queryCountryAliases: ArcgisCountryAliases[] = [
    { code: 'ESP', aliases: ['espana', 'spain', 'esp'] },
    { code: 'MEX', aliases: ['mexico', 'mex'] },
    { code: 'USA', aliases: ['estados unidos', 'eeuu', 'usa', 'u s a', 'united states'] },
    { code: 'GBR', aliases: ['reino unido', 'uk', 'gbr', 'united kingdom', 'great britain'] },
    { code: 'PRT', aliases: ['portugal', 'prt'] },
    { code: 'FRA', aliases: ['francia', 'france', 'fra'] },
    { code: 'DEU', aliases: ['alemania', 'germany', 'deu'] },
    { code: 'ITA', aliases: ['italia', 'italy', 'ita'] },
    { code: 'ARG', aliases: ['argentina', 'arg'] },
    { code: 'CHL', aliases: ['chile', 'chl'] },
    { code: 'COL', aliases: ['colombia', 'col'] },
    { code: 'PER', aliases: ['peru', 'per'] },
  ];
  private readonly countryDefaultViews: Record<string, ArcgisCoordinate & { zoom: number }> = {
    ESP: { lat: 40.4168, lng: -3.7038, zoom: 6 },
    MEX: { lat: 23.6345, lng: -102.5528, zoom: 5 },
    USA: { lat: 39.8283, lng: -98.5795, zoom: 4 },
    GBR: { lat: 54.7024, lng: -3.2766, zoom: 6 },
    PRT: { lat: 39.3999, lng: -8.2245, zoom: 7 },
    FRA: { lat: 46.6034, lng: 1.8883, zoom: 6 },
    DEU: { lat: 51.1657, lng: 10.4515, zoom: 6 },
    ITA: { lat: 41.8719, lng: 12.5674, zoom: 6 },
    ARG: { lat: -38.4161, lng: -63.6167, zoom: 4 },
    CHL: { lat: -35.6751, lng: -71.5430, zoom: 4 },
    COL: { lat: 4.5709, lng: -74.2973, zoom: 5 },
    PER: { lat: -9.19, lng: -75.0152, zoom: 5 },
  };
  private readonly modalZoomOptions: Record<ArcgisMapZoomPreset, ArcgisMapZoomOptions> = {
    // Opcion A: comportamiento anterior de la modal.
    // Se deja como referencia por si en el futuro se quiere volver a un zoom mas libre.
    withoutZoomRestrictions: {},
    // Opcion B: restriccion indicada por el companero. Es la opcion activa.
    withZoomRestrictions: {
      minZoom: 14,
      maxZoom: 19,
    },
  };

  constructor(private http: HttpClient) {}

  getModalZoomOptions(preset: ArcgisMapZoomPreset = 'withZoomRestrictions'): ArcgisMapZoomOptions {
    return this.modalZoomOptions[preset];
  }

  /**
   * Vista inicial del mapa cuando aún no hay ninguna ubicación guardada: centra
   * en el país detectado por el idioma/región del navegador (sin pedir permiso
   * de geolocalización), con un zoom que muestre el país completo. Si no se
   * reconoce el país, cae a España.
   */
  getDefaultMapView(): ArcgisCoordinate & { zoom: number } {
    const countryCode = this.getBrowserCountryCode();
    return (countryCode && this.countryDefaultViews[countryCode]) || this.countryDefaultViews.ESP;
  }

  getSatelliteTileUrl(): string {
    const apiKey = this.apiKey;
    if (!apiKey) {
      this.warnMissingApiKey('mapa satelite');
    }
    return this.withToken(this.satelliteTilesUrl);
  }

  searchAddress(query: string): Observable<ArcgisSearchResult[]> {
    const apiKey = this.apiKey;

    if (!apiKey) {
      this.warnMissingApiKey('busqueda de direcciones');
      return throwError(() => new Error('ArcGIS API key is not configured.'));
    }

    const sourceCountry = this.getCountryCodeFromQuery(query) ?? this.getBrowserCountryCode();

    if (!sourceCountry) {
      return this.findAddressCandidates(query);
    }

    return this.findAddressCandidates(query, sourceCountry).pipe(
      switchMap((results) => results.length ? of(results) : this.findAddressCandidates(query)),
    );
  }

  private findAddressCandidates(query: string, sourceCountry?: string): Observable<ArcgisSearchResult[]> {
    let params = new HttpParams()
      .set('f', 'json')
      .set('SingleLine', query)
      .set('maxLocations', '1')
      .set('outFields', 'Match_addr,Addr_type')
      .set('token', this.apiKey);

    if (sourceCountry) {
      params = params.set('sourceCountry', sourceCountry);
    }

    return this.http.get<ArcgisGeocodeResponse>(this.geocodeUrl, { params }).pipe(
      map((response) =>
        (response.candidates ?? [])
          .filter((candidate) => Number.isFinite(candidate.location?.y) && Number.isFinite(candidate.location?.x))
          .map((candidate) => ({
            ...this.normalizeCoordinate({
              lat: candidate.location.y,
              lng: candidate.location.x,
            }),
            label: candidate.address,
          })),
      ),
    );
  }

  reverseGeocode(coordinate: ArcgisCoordinate): Observable<ArcgisReverseGeocodeResult> {
    const apiKey = this.apiKey;

    if (!apiKey) {
      this.warnMissingApiKey('geocodificacion inversa');
      return throwError(() => new Error('ArcGIS API key is not configured.'));
    }

    const normalizedCoordinate = this.normalizeCoordinate(coordinate);
    const params = new HttpParams()
      .set('f', 'json')
      .set('location', `${normalizedCoordinate.lng},${normalizedCoordinate.lat}`)
      .set('outFields', 'City,District,Subregion,Region')
      .set('token', apiKey);

    return this.http.get<ArcgisReverseGeocodeResponse>(this.reverseGeocodeUrl, { params }).pipe(
      map((response) => {
        const address = response.address ?? {};
        return {
          city: this.cleanAddressValue(address.City) || this.cleanAddressValue(address.District),
          province: this.cleanAddressValue(address.Subregion) || this.cleanAddressValue(address.Region),
        };
      }),
    );
  }

  parseCoordinates(value: string): ArcgisCoordinate | null {
    const normalized = value.trim().replace(';', ',');
    const match = normalized.match(/^(-?\d+(?:[.,]\d+)?)\s*,\s*(-?\d+(?:[.,]\d+)?)$/);
    if (!match) return null;

    const first = Number(match[1].replace(',', '.'));
    const second = Number(match[2].replace(',', '.'));

    if (!Number.isFinite(first) || !Number.isFinite(second)) return null;

    const lat = first;
    const lng = second;
    const isValidLatitude = lat >= -90 && lat <= 90;
    const isValidLongitude = lng >= -180 && lng <= 180;

    return isValidLatitude && isValidLongitude ? this.normalizeCoordinate({ lat, lng }) : null;
  }

  normalizeCoordinate(coordinate: ArcgisCoordinate): ArcgisCoordinate {
    return {
      lat: this.roundCoordinate(coordinate.lat),
      lng: this.roundCoordinate(coordinate.lng),
    };
  }

  normalizeGeoJsonCoordinates(coordinates: number[][][]): number[][][] {
    return coordinates.map((ring) =>
      ring.map((position) =>
        position.map((coordinate) => this.roundCoordinate(coordinate)),
      ),
    );
  }

  roundCoordinate(value: number): number {
    const factor = 10 ** this.coordinateDecimals;
    return Math.round(value * factor) / factor;
  }

  private get apiKey(): string {
    return environment.arcgis?.apiKey?.trim() ?? '';
  }

  private withToken(url: string): string {
    const apiKey = this.apiKey;
    return apiKey ? `${url}?token=${encodeURIComponent(apiKey)}` : url;
  }

  private warnMissingApiKey(context: string): void {
    if (environment.production) return;

    console.warn(
      `[ArcGIS] Falta environment.arcgis.apiKey para ${context}. ` +
      'Revisa el environment activo y configura la API key de ArcGIS.',
    );
  }

  private cleanAddressValue(value?: string): string {
    return value?.trim() ?? '';
  }

  private getCountryCodeFromQuery(query: string): string | null {
    const normalizedQuery = ` ${this.normalizeSearchText(query)} `;
    const match = this.queryCountryAliases.find((country) =>
      country.aliases.some((alias) => normalizedQuery.includes(` ${this.normalizeSearchText(alias)} `)),
    );

    return match?.code ?? null;
  }

  private getBrowserCountryCode(): string | null {
    if (typeof navigator === 'undefined') return null;

    const languages = [
      ...(navigator.languages ?? []),
      navigator.language,
    ].filter((language): language is string => !!language);

    for (const language of languages) {
      const parts = language.split('-');
      const region = parts[1]?.toUpperCase();
      const languageCode = parts[0]?.toLowerCase();

      if (region && this.browserRegionCountryCodes[region]) {
        return this.browserRegionCountryCodes[region];
      }

      if (languageCode && this.browserLanguageCountryCodes[languageCode]) {
        return this.browserLanguageCountryCodes[languageCode];
      }
    }

    return null;
  }

  private normalizeSearchText(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }
}
